import { t } from "@lingui/macro";
import {
  DiamondsFourIcon,
  FileArrowDownIcon,
  HeadsetIcon,
  ShieldCheckIcon,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@reactive-resume/ui";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

import { useAccountCurrency } from "@/client/services/currency/currency";
import { useRatePay } from "@/client/services/rate/rates";
import { useUser } from "@/client/services/user/user";
import { useDialog } from "@/client/stores/dialog";

import { AccountCurrencyNote } from "./forfait/account-currency-note";
import { CvOfferSelector, type CvOfferPlan } from "./forfait/cv-offer-selector";

type ForfaitDialogStep = "loading" | "offers";

export const ForfaitDialog = () => {
  const offerGuarantees = [
    {
      icon: FileArrowDownIcon,
      label: t`PDF (.pdf) haute qualité`,
    },
    {
      icon: DiamondsFourIcon,
      label: "Modèles professionnels",
    },
    {
      icon: ShieldCheckIcon,
      label: "Paiement sécurisé",
    },
    {
      icon: HeadsetIcon,
      label: "Support MonCVPro",
    },
  ];

  const reduceMotion = useReducedMotion();

  const { isOpen, close } = useDialog("forfait");

  const { open: openPayment } = useDialog("payment");

  // Le pays est choisi juste après la connexion : on attend seulement le compte.
  const { loading: userLoading } = useUser();

  const [step, setStep] = useState<ForfaitDialogStep>("loading");

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setStep(userLoading ? "loading" : "offers");
  }, [isOpen, userLoading]);

  /*
   * Les offres ne sont chargées que lorsque
   * leur étape est réellement affichée.
   */
  const shouldLoadOffers = isOpen && step === "offers";

  const { rates, error, isLoading } = useRatePay(shouldLoadOffers);

  const { format: formatPrice } = useAccountCurrency();

  /*
   * Seules les offres possédant des crédits
   * de téléchargement CV apparaissent ici.
   *
   * Les packs exclusivement IA restent exclus.
   */
  const plans = useMemo<CvOfferPlan[]>(
    () =>
      (rates?.data ?? [])
        .filter((plan) => Number(plan.credit) > 0)
        .map((plan) => ({
          id: plan.id,
          name: plan.name,
          price: plan.price,
          credits: plan.credit,
          validity: plan.validity,
          description: plan.description,
          recommended: plan.isRecommended ?? false,
        })),
    [rates?.data],
  );

  const continueToPayment = (plan: CvOfferPlan) => {
    close();

    openPayment("create", {
      id: "payment",
      item: {
        id: plan.id,
        source: "offer",
        returnTo: "cv-offers",

        /*
         * Ces informations sont utilisées
         * uniquement par l’affichage.
         *
         * Le backend continue de recevoir
         * exclusivement l’identifiant du plan.
         */
        product: {
          id: plan.id,
          name: plan.name,
          price: plan.price,
          credits: plan.credits,
          validity: plan.validity,
          description: plan.description,
          recommended: plan.recommended,
        },
      },
    });
  };

  const compactDialog = step !== "offers";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          close();
        }
      }}
    >
      <DialogContent
        className={
          compactDialog
            ? [
                "w-[calc(100vw-1rem)]",
                "max-w-[600px]",
                "max-h-[calc(100dvh-1rem)]",
                "overflow-y-auto",
                "rounded-[26px]",
                "border-0 bg-transparent",
                "p-2 shadow-none",
                "sm:w-[calc(100vw-2rem)]",
                "[scrollbar-width:thin]",
                "[scrollbar-color:#6366f1_transparent]",
                "[&::-webkit-scrollbar]:w-1.5",
                "[&::-webkit-scrollbar-thumb]:rounded-full",
                "[&::-webkit-scrollbar-thumb]:bg-indigo-400",
              ].join(" ")
            : [
                "flex w-[calc(100vw-1rem)]",
                "max-w-6xl flex-col",
                "max-h-[calc(100dvh-1rem)]",
                "overflow-hidden",
                "rounded-2xl border-0",
                "bg-[#f8f7ff] p-0",
                "shadow-2xl",
                "sm:w-[calc(100vw-2rem)]",
              ].join(" ")
        }
      >
        <AnimatePresence mode="wait" initial={false}>
          {step === "loading" ? (
            <motion.div
              key="loading-country"
              className={[
                "relative mx-auto grid",
                "min-h-[310px] w-full",
                "max-w-[560px]",
                "place-items-center",
                "overflow-hidden",
                "rounded-[24px]",
                "border border-indigo-100",
                "bg-white px-6 py-10",
                "shadow-[0_24px_70px_rgba(39,48,112,0.16)]",
              ].join(" ")}
              initial={
                reduceMotion
                  ? false
                  : {
                      opacity: 0,
                      scale: 0.98,
                    }
              }
              animate={{
                opacity: 1,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.98,
              }}
            >
              <div
                aria-hidden="true"
                className={[
                  "pointer-events-none",
                  "absolute -right-20 -top-20",
                  "size-48 rounded-full",
                  "bg-indigo-200/30 blur-3xl",
                ].join(" ")}
              />

              <div className="relative text-center">
                <span
                  className={[
                    "mx-auto grid size-12",
                    "place-items-center",
                    "rounded-2xl",
                    "bg-gradient-to-br",
                    "from-indigo-100",
                    "to-emerald-100",
                    "text-indigo-700",
                  ].join(" ")}
                >
                  <span
                    aria-hidden="true"
                    className={[
                      "size-5 animate-spin",
                      "rounded-full border-2",
                      "border-indigo-200",
                      "border-t-indigo-700",
                    ].join(" ")}
                  />
                </span>

                <DialogTitle
                  className={["mt-4 text-lg", "font-bold tracking-tight", "text-[#071451]"].join(
                    " ",
                  )}
                >
                  Préparation de vos offres
                </DialogTitle>

                <DialogDescription
                  className={["mt-1.5 text-xs", "leading-5 text-slate-500"].join(" ")}
                >
                  Nous récupérons votre compte et votre devise d’affichage.
                </DialogDescription>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="offers"
              className={["flex min-h-0", "flex-1 flex-col", "overflow-hidden"].join(" ")}
              initial={
                reduceMotion
                  ? false
                  : {
                      opacity: 0,
                      x: 18,
                    }
              }
              animate={{
                opacity: 1,
                x: 0,
              }}
              exit={{
                opacity: 0,
                x: 12,
              }}
              transition={{
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <header
                className={[
                  "shrink-0 border-b",
                  "border-indigo-100",
                  "bg-white px-5 py-3",
                  "pr-12 text-center",
                  "sm:px-7 sm:py-3.5",
                ].join(" ")}
              >
                <span
                  className={[
                    "mx-auto grid size-9",
                    "place-items-center",
                    "rounded-xl",
                    "bg-gradient-to-br",
                    "from-indigo-100",
                    "to-emerald-100",
                    "text-indigo-700",
                  ].join(" ")}
                >
                  <DiamondsFourIcon aria-hidden size={19} weight="duotone" />
                </span>

                <DialogTitle
                  className={[
                    "mt-1.5 text-xl",
                    "font-bold tracking-tight",
                    "text-[#071451]",
                    "sm:text-2xl",
                  ].join(" ")}
                >
                  Choisissez votre offre CV
                </DialogTitle>

                <DialogDescription
                  className={[
                    "mx-auto mt-1",
                    "max-w-2xl",
                    "text-xs leading-5",
                    "text-slate-500",
                  ].join(" ")}
                >
                  Comparez les trois formules puis choisissez directement votre offre.
                </DialogDescription>
              </header>

              <div
                className={[
                  "min-h-0 flex-1",
                  "overflow-y-auto",
                  "p-3.5 sm:p-4",
                  "[scrollbar-color:#6366f1_#eef2ff]",
                  "[scrollbar-width:thin]",
                  "[&::-webkit-scrollbar]:w-1.5",
                  "[&::-webkit-scrollbar-track]:rounded-full",
                  "[&::-webkit-scrollbar-track]:bg-indigo-50",
                  "[&::-webkit-scrollbar-thumb]:rounded-full",
                  "[&::-webkit-scrollbar-thumb]:bg-indigo-400",
                ].join(" ")}
              >
                <AccountCurrencyNote onNavigate={close} />

                <div className="mt-3">
                  {isLoading ? (
                    <div className="grid gap-3 md:grid-cols-3">
                      {[0, 1, 2].map((item) => (
                        <div
                          key={item}
                          className={["h-[290px]", "animate-pulse", "rounded-2xl", "bg-white"].join(
                            " ",
                          )}
                        />
                      ))}
                    </div>
                  ) : error ? (
                    <div
                      className={[
                        "grid min-h-52",
                        "place-items-center",
                        "rounded-2xl",
                        "border border-rose-200",
                        "bg-rose-50 px-5",
                        "text-center",
                      ].join(" ")}
                    >
                      <div>
                        <h2 className="text-sm font-bold text-rose-700">
                          Impossible de charger les offres CV
                        </h2>

                        <p className="mt-1 text-xs text-rose-600">
                          Vérifiez votre connexion puis rouvrez cette fenêtre.
                        </p>
                      </div>
                    </div>
                  ) : plans.length === 0 ? (
                    <div
                      className={[
                        "grid min-h-52",
                        "place-items-center",
                        "rounded-2xl",
                        "border border-dashed",
                        "border-indigo-200",
                        "bg-white px-5",
                        "text-center",
                      ].join(" ")}
                    >
                      <div>
                        <h2 className="text-sm font-bold text-[#071451]">
                          Aucune offre CV disponible
                        </h2>

                        <p className="mt-1 text-xs text-slate-500">
                          Les packs IA ne sont pas affichés dans cette fenêtre.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <CvOfferSelector
                      plans={plans}
                      formatPrice={formatPrice}
                      onContinue={continueToPayment}
                    />
                  )}
                </div>

                {!isLoading && !error && plans.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {offerGuarantees.map((item) => (
                      <div
                        key={item.label}
                        className={[
                          "flex min-h-9",
                          "items-center gap-2",
                          "rounded-xl",
                          "border border-indigo-100",
                          "bg-white px-3 py-2",
                          "text-[9px]",
                          "font-semibold",
                          "text-slate-600",
                          "sm:text-[10px]",
                        ].join(" ")}
                      >
                        <item.icon aria-hidden size={14} className="shrink-0 text-indigo-600" />

                        {item.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
